import { useState, useEffect } from "react";
import { getBezierPath, getStraightPath, getSmoothStepPath, EdgeLabelRenderer } from "reactflow";
import { Pencil, Trash2 } from "lucide-react";
import { RELATIONSHIP_TYPES, buildRelationshipSentence } from "@/constants/archimate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";

function computePath(lineStyle, params) {
  if (lineStyle === "orthogonal") {
    return getSmoothStepPath({ ...params, borderRadius: 0 });
  }
  if (lineStyle === "straight") {
    return getStraightPath(params);
  }
  return getBezierPath({ ...params, curvature: 0.25 });
}

export function ArchiEdge({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, data, selected, style }) {
  const [edgePath, labelX, labelY] = computePath(data?.lineStyle, {
    sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition,
  });
  const [mode, setMode] = useState("view");
  const [editType, setEditType] = useState(data?.relType);
  const [editName, setEditName] = useState(data?.name || "");
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (data?.open) {
      setMode("view");
      setEditType(data?.relType);
      setEditName(data?.name || "");
    }
  }, [data?.open, data?.relType, data?.name]);

  const relInfo = RELATIONSHIP_TYPES[data?.relType] || {};
  const markerStart = relInfo.markerStart ? `url(#archi-${relInfo.markerStart})` : undefined;
  const markerEnd = relInfo.markerEnd ? `url(#archi-${relInfo.markerEnd})` : undefined;
  const opacity = style?.opacity ?? 1;
  const sentence = buildRelationshipSentence(data?.relType, data?.sourceLabel || "This element", data?.targetLabel || "that element", data?.name);

  const handleSave = () => {
    data?.onUpdate?.({ type: editType, name: editName });
    setMode("view");
  };

  return (
    <>
      <path
        id={id}
        d={edgePath}
        fill="none"
        stroke={selected ? "#2563EB" : "#475569"}
        strokeWidth={selected ? 2.5 : 1.75}
        strokeDasharray={relInfo.dash && relInfo.dash !== "0" ? relInfo.dash : undefined}
        markerStart={markerStart}
        markerEnd={markerEnd}
        style={{ opacity, transition: "opacity 0.2s ease" }}
        data-testid={`archimate-edge-${data?.relType}-${id}`}
      />
      <path
        d={edgePath}
        fill="none"
        stroke="transparent"
        strokeWidth={10}
        style={{ pointerEvents: "stroke" }}
        data-testid={`archimate-edge-hitbox-${id}`}
      />
      <EdgeLabelRenderer>
        <div
          style={{
            position: "absolute",
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            pointerEvents: "none",
            opacity,
            transition: "opacity 0.2s ease",
          }}
          className="bg-white/90 px-1.5 py-0.5 rounded text-[9px] font-medium text-slate-600 border border-slate-200 whitespace-nowrap"
        >
          {relInfo.label}
          {data?.name ? `: ${data.name}` : ""}
        </div>
        {data?.open && (
          <div
            data-testid={`relationship-popover-${id}`}
            style={{
              position: "absolute",
              transform: `translate(-50%, 0%) translate(${labelX}px,${labelY + 16}px)`,
              pointerEvents: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
            className="z-30 bg-white border border-slate-200 shadow-lg rounded-md px-3 py-2 text-xs text-slate-700 leading-relaxed max-w-[260px]"
          >
            {mode === "view" ? (
              <>
                <p>{sentence}</p>
                {!data?.readOnly && (
                  <div className="flex items-center gap-3 mt-2 pt-2 border-t border-slate-100">
                    <button
                      data-testid={`relationship-edit-button-${id}`}
                      onClick={() => setMode("edit")}
                      className="flex items-center gap-1 text-[11px] font-medium text-blue-600 hover:text-blue-700"
                    >
                      <Pencil size={11} /> Edit
                    </button>
                    <button
                      data-testid={`relationship-delete-button-${id}`}
                      onClick={() => setConfirmDelete(true)}
                      className="flex items-center gap-1 text-[11px] font-medium text-rose-600 hover:text-rose-700"
                    >
                      <Trash2 size={11} /> Delete
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="w-56 space-y-2">
                <div>
                  <label className="text-[10px] font-medium text-slate-500">Relationship Type</label>
                  <Select value={editType} onValueChange={setEditType}>
                    <SelectTrigger data-testid={`relationship-edit-type-select-${id}`} className="h-7 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(RELATIONSHIP_TYPES).map(([key, v]) => (
                        <SelectItem key={key} value={key}>
                          {v.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[10px] font-medium text-slate-500">Label (optional)</label>
                  <Input
                    data-testid={`relationship-edit-name-input-${id}`}
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="h-7 text-xs"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-1">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-6 px-2 text-[11px]"
                    onClick={() => {
                      setEditType(data?.relType);
                      setEditName(data?.name || "");
                      setMode("view");
                    }}
                    data-testid={`relationship-edit-cancel-button-${id}`}
                  >
                    Cancel
                  </Button>
                  <Button size="sm" className="h-6 px-2 text-[11px]" onClick={handleSave} data-testid={`relationship-edit-save-button-${id}`}>
                    Save
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </EdgeLabelRenderer>
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent data-testid={`relationship-delete-confirm-dialog-${id}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this relationship?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the "{relInfo.label}" relationship between {data?.sourceLabel} and {data?.targetLabel}. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`relationship-delete-cancel-button-${id}`}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid={`relationship-delete-confirm-button-${id}`}
              onClick={() => {
                setConfirmDelete(false);
                data?.onDelete?.();
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
